class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartDrawer = this.closest('cart-drawer');
      cartDrawer.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = document.getElementById('cart-drawer');
    this.header = document.querySelector('sticky-header');
    this.cartDrawerOpen = document.querySelector('[data-js-cart-drawer="cartDrawerOpen"]');
    this.cartDrawerClose = document.querySelector('[data-js-cart-drawer="cartDrawerClose"]');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.lineItemStatusElement = document.getElementById('cart-drawer-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.drawer.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    // Delegation survives the section HTML replacement after quantity changes.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-js-cart-drawer="cartDrawerClose"]')) {
        event.preventDefault();
        this.close();
      }
    });

    document.querySelectorAll('[data-js-cart-drawer="cartDrawerOpen"]').forEach((openButton) => {
      openButton.addEventListener('click', this.handleDrawerOpen.bind(this))
    });
  }

  onChange(event) {
    if (event.target.matches('[data-cart-option-select]')) {
      this.onVariantOptionChange(event.target);
      return;
    }

    if (!event.target.classList.contains('quantity__input')) return;

    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onVariantOptionChange(select) {
    const optionsWrapper = select.closest('[data-cart-item-options]');
    if (!optionsWrapper) return;

    const cartItem = select.closest('.cart-item');
    const variantsEl = optionsWrapper.querySelector('[data-cart-item-variants]');
    const propertiesEl = optionsWrapper.querySelector('[data-cart-item-properties]');
    if (!variantsEl || !cartItem) return;

    let variants = [];
    let properties = {};

    try {
      variants = JSON.parse(variantsEl.textContent);
      if (propertiesEl) properties = JSON.parse(propertiesEl.textContent);
    } catch (error) {
      window.console.error(error);
      return;
    }

    const selectedOptions = Array.from(optionsWrapper.querySelectorAll('[data-cart-option-select]'))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((optionSelect) => optionSelect.value);

    const matchedVariant = variants.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((option, index) => option === selectedOptions[index])
    ));

    const line = optionsWrapper.dataset.index;
    const previousValue = select.dataset.previousValue || matchedVariant?.options?.[Number(select.dataset.optionIndex)];

    if (!matchedVariant) {
      select.value = previousValue;
      this.showLineError(line, window.cartStrings.error);
      return;
    }

    if (!matchedVariant.available) {
      select.value = previousValue;
      this.showLineError(line, window.cartStrings.error);
      return;
    }

    if (String(matchedVariant.id) === String(cartItem.dataset.id)) {
      select.dataset.previousValue = select.value;
      return;
    }

    const previousValueToRestore = select.dataset.previousValue;
    select.dataset.previousValue = select.value;

    this.changeVariant({
      line,
      key: optionsWrapper.dataset.key,
      oldVariantId: Number(cartItem.dataset.id),
      newVariantId: matchedVariant.id,
      quantity: Number(optionsWrapper.dataset.quantity) || 1,
      properties,
      sellingPlan: optionsWrapper.dataset.sellingPlan || null,
      onError: () => {
        select.value = previousValueToRestore;
        select.dataset.previousValue = previousValueToRestore;
      }
    });
  }

  showLineError(line, message) {
    const lineItem = document.getElementById(`CartDrawerItem-${line}`);
    if (!lineItem) return;

    const errorText = lineItem.querySelector('.cart-item__error-text');
    if (errorText) errorText.innerHTML = message;
  }

  changeVariant({ line, key, oldVariantId, newVariantId, quantity, properties, sellingPlan, onError }) {
    this.enableLoading(line);

    const buildItemPayload = (variantId) => {
      const itemPayload = {
        id: variantId,
        quantity,
        properties: properties || {}
      };

      if (sellingPlan) {
        itemPayload.selling_plan = Number(sellingPlan);
      }

      return itemPayload;
    };

    const sectionIds = this.getSectionsToRender().map((section) => section.section);

    // Remove first, then add — avoids inventory/qty limits that block add-then-remove.
    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then((response) => response.json())
      .then((removeState) => {
        if (removeState.errors) {
          if (typeof onError === 'function') onError();
          this.showLineError(line, removeState.errors);
          this.disableLoading();
          return null;
        }

        return fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [buildItemPayload(newVariantId)],
            sections: sectionIds,
            sections_url: window.location.pathname
          })
        });
      })
      .then((response) => {
        if (!response) return null;
        return response.json();
      })
      .then((addResponse) => {
        if (!addResponse) return null;

        if (addResponse.status) {
          return fetch(`${routes.cart_add_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [buildItemPayload(oldVariantId)],
              sections: sectionIds,
              sections_url: window.location.pathname
            })
          })
            .then((response) => response.json())
            .then((restoreResponse) => {
              if (typeof onError === 'function') onError();
              this.showLineError(line, addResponse.description || window.cartStrings.error);

              if (restoreResponse && restoreResponse.sections) {
                return this.getCart().then((cartData) => {
                  this.renderContents(cartData, restoreResponse, line, '', true, true);
                });
              }

              this.disableLoading();
              return null;
            });
        }

        return this.getCart().then((cartData) => {
          this.renderContents(cartData, addResponse, line, '', true, true);
        });
      })
      .catch(() => {
        if (typeof onError === 'function') onError();
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-drawer-errors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  /**
   * Open the cart-drawer
   */
  handleDrawerOpen(event) {
    if (theme.pageType === 'cart') {
      return;
    }

    event?.preventDefault();
    if (this.header) this.header.reveal();
    this.open();
  }

  /**
   * Triggered whenever product-form was submitted and item was added to cart
   * @param  {Object} Added to cart item data
   */
  handleAddedItem(addedItemResponse) {
    const fetchedCartData = this.getCart();
    this.handleCartData(fetchedCartData, addedItemResponse);
  }

  /**
   * Get Cart data from Shopify Ajax API
   * @return {Object} Promise
   */
  getCart() {
    return new Promise((resolve, reject) => {
      fetch(`${routes.ajax_api}`)
        .then((response) => response.json())
        .then((response) => {
          if (response) {
            resolve(response);
            return;
          }

          reject(response);
        })
        .catch((e) => {
          window.console.error(error);
        });
    });
  }

  /**
   * Handle cart data after the response from Shopify Ajax API
   * @param  {Object} Promise
   * @param  {Object} Added to cart item data
   */
  handleCartData(promise, addedItemResponse = false) {
    Promise.all([promise]).then((values) => {
      if (values.length === 0) {
        return;
      }

      const cartData = values[0];

      if (addedItemResponse !== false) {
        const line = cartData.items.map((item, index) => {
          if (item.id === addedItemResponse.id) {
            return index;
          }
        });

        this.renderContents(cartData, addedItemResponse, line, '', false);
      }
    })
    .catch((error) => {
      window.console.error(error);
    });
  }

  /**
   * Get sections to render
   * @return {Object}
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: document.getElementById('cart-drawer').dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.renderContents(parsedState, false, line, name, true);

        if (quantity == 0) {
          this.updateTrappedFocus();
        }
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        document.getElementById('cart-drawer-errors').textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  open() {
    document.dispatchEvent(new CustomEvent('kalini:cart-open'));
    this.activeElement = document.activeElement || this.cartDrawerOpen;
    this.drawer.classList.add('animate', 'active');

    this.drawer.addEventListener('transitionend', () => {
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    }, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.drawer.classList.remove('active');

    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  /**
   * Render Contents
   * @param  {Object} Cart data
   * @param  {Object/Boolean} Added to cart item data / False
   * @param  {Number}  Line item index
   * @param  {String}  Line item input name
   * @param  {Boolean} Whether Cart-drawer is opened
   * @param  {Boolean} Skip quantity mismatch error (e.g. variant swap)
   */
  renderContents(parsedState, addedItemResponse = false, line, name, isDrawerOpened = false, skipQuantityError = false) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartFooter = document.getElementById('cart-drawer-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section => {
      let state = parsedState;

      if (addedItemResponse !== false) {
        state = addedItemResponse;
      }

      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      elementToReplace.innerHTML =
        this.getSectionInnerHTML(state.sections[section.section], section.selector);
    }));

    document.querySelectorAll('.header__icons-cart-count').forEach((count) => {
      count.textContent = parsedState.item_count;
    });
    this.updateLiveRegions(line, parsedState.item_count, skipQuantityError);
    const lineItem =  document.getElementById(`CartDrawerItem-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();
    this.disableLoading();

    if (!isDrawerOpened) {
      if (this.header) this.header.reveal();
      this.open();
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount, skipQuantityError = false) {
    if (line == '' || !line) {
      this.currentItemCount = itemCount;
      return;
    }

    const lineError = document.getElementById(`CartDrawerItem-error-${line}`);
    const quantityInput = document.getElementById(`CartDrawerItem-quantity-${line}`);

    if (!skipQuantityError && this.currentItemCount === itemCount && lineError && quantityInput) {
      lineError.querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace(
        '[quantity]',
        quantityInput.value
      );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-drawer-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    document.getElementById('cart-drawer').classList.add('cart-drawer--disabled');
    this.querySelectorAll(`#CartDrawerItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document.getElementById('cart-drawer').classList.remove('cart-drawer--disabled');
  }

  handleBodyClick(event) {
    const target = event.target;
    const isNotDrawer = (target !== this.drawer) && (!target.closest('cart-drawer'));
    const isNotButtonOpen = target !== this.cartDrawerOpen && !target.closest('[data-js-cart-drawer="cartDrawerOpen"]');
    const isCloseButton = (target === this.cartDrawerClose) || (target.closest('[data-js-cart-drawer="cartDrawerClose"]'));

    if (isNotDrawer && isNotButtonOpen || isCloseButton) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateTrappedFocus() {
    removeTrapFocus(document.activeElement);

    setTimeout(() => {
      this.setActiveElement(this.cartDrawerClose || this.cartDrawerOpen);
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
